import { FarmerOffersPage } from '@components/shared/FarmerOffersPage';
import { useAppbarTitle } from '@hooks/useAppbarTitle';
import { usePageTitle } from '@hooks/usePageTitle';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OffersList, RequestsList } from '../components';
import { getOffersForRequest, getRequests, useFarmerSeasonalOneTimeOffersStore } from '../services';

export const SeasonalOneTimeOffersPage: React.FC = () => {
	const { t } = useTranslation();
	const [currentStatus, setCurrentStatus] = useState<string>('ongoing');

	useAppbarTitle(t('requestsAndOffers.oneTimeSeasonal'));
	usePageTitle(t('requestsAndOffers.oneTimeSeasonal'));

	const {
		requests,
		isRequestsLoading,
		requestsError,
		selectedRequestId,
		offers,
		isOffersLoading,
		offersError,
		setSelectedRequestId,
		setRequests,
		setIsRequestsLoading,
		setRequestsError,
		setOffers,
		setIsOffersLoading,
		setOffersError,
		resetState,
	} = useFarmerSeasonalOneTimeOffersStore();
	
	// Function to refresh offers for the selected request
	const refreshOffers = useCallback(() => {
		if (selectedRequestId) {
			// Set loading state
			setIsOffersLoading(true);
			
			console.log('Refreshing offers with a slight delay to prevent race conditions');
			
			// Add a slight delay before refreshing to prevent race conditions
			setTimeout(() => {
				// Fetch offers for the selected request
				getOffersForRequest(selectedRequestId)
					.then((response) => {
						setOffers(response.data.offers);
						console.log('Offers refreshed successfully');
					})
					.catch((error) => {
						console.error('Error refreshing offers:', error);
						setOffersError(error instanceof Error ? error.message : 'Error refreshing offers');
					})
					.finally(() => {
						setIsOffersLoading(false);
					});
			}, 500); // 500ms delay
		}
	}, [selectedRequestId, setIsOffersLoading, setOffers, setOffersError]);
	
	// Function to refresh the main requests list
	const refreshRequests = useCallback(() => {
		// Set loading state
		setIsRequestsLoading(true);
		
		console.log('Refreshing requests list from endpoint: requests/v1/farmer-request-and-offer/one_time_and_seasonal');
		
		// Add a slight delay before refreshing to prevent race conditions
		setTimeout(() => {
			// Fetch requests with current status
			getRequests(currentStatus as any)
				.then((response) => {
					setRequests(response.data.requests);
					console.log('Requests refreshed successfully');
					
					// If we have a selected request, also refresh its offers
					if (selectedRequestId) {
						refreshOffers();
					}
				})
				.catch((error) => {
					console.error('Error refreshing requests:', error);
					setRequestsError(error instanceof Error ? error.message : 'Error refreshing requests');
				})
				.finally(() => {
					setIsRequestsLoading(false);
				});
		}, 500); // 500ms delay
	}, [currentStatus, selectedRequestId, setIsRequestsLoading, setRequests, setRequestsError]);
	
	// Enhanced function to refresh both requests and offers
	const refreshAll = useCallback(() => {
		console.log('Refreshing both requests and offers');
		refreshRequests();
	}, [refreshRequests]);
	
	// Create a wrapper for getOffersForRequest that ignores the second parameter
	const getOffersForRequestWrapper = useCallback(
		(requestId: string, _serviceId?: string) => getOffersForRequest(requestId),
		[]
	);

	return (
		<FarmerOffersPage
			pageTitle={t('requestsAndOffers.oneTimeSeasonal')}
			requests={requests}
			isRequestsLoading={isRequestsLoading}
			requestsError={requestsError}
			selectedRequestId={selectedRequestId}
			offers={offers}
			isOffersLoading={isOffersLoading}
			offersError={offersError}
			setSelectedRequestId={setSelectedRequestId}
			setRequests={setRequests}
			setIsRequestsLoading={setIsRequestsLoading}
			setRequestsError={setRequestsError}
			setOffers={setOffers}
			setIsOffersLoading={setIsOffersLoading}
			setOffersError={setOffersError}
			resetState={resetState}
			getRequests={getRequests}
			getOffersForRequest={getOffersForRequestWrapper}
			RequestsList={(props) => <RequestsList {...props} onRefreshOffers={refreshOffers} />}
			OffersList={(props) => <OffersList {...props} onRefreshOffers={refreshAll} />}
			hasOffersPanel={true}
			defaultStatus="ongoing"
			onStatusChange={setCurrentStatus}
		/>
	);
};
